/**
 * Domain errors are values, not exceptions.
 *
 * Validation returns something the UI can render, so a form can show every
 * problem at once rather than revealing them one reload at a time. Throwing is
 * reserved for genuine programmer error — a bug, not a bad input.
 */

export interface ValidationError {
  /** Machine-readable, for tests and conditional UI. */
  readonly code: string
  /** Which input this is about, where there is one. */
  readonly field: string | undefined
  /** Human-readable, safe to show a user as-is. */
  readonly message: string
}

export type Result<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly errors: readonly ValidationError[] }

export function ok<T>(value: T): Result<T> {
  return { ok: true, value }
}

export function err<T>(...errors: readonly ValidationError[]): Result<T> {
  return { ok: false, errors }
}

export function invalid(code: string, message: string, field?: string): ValidationError {
  return { code, field, message }
}

/**
 * Collects every failure rather than stopping at the first.
 *
 * Fixing a form one error per attempt is exactly the friction that erodes daily
 * logging, and adherence is the product requirement the domain model exists to
 * serve.
 */
export function combine<T>(results: {
  [K in keyof T]: Result<T[K]>
}): Result<T> {
  const errors: ValidationError[] = []
  const value = {} as T

  for (const key of Object.keys(results) as (keyof T)[]) {
    const result = results[key]
    if (result.ok) {
      value[key] = result.value
    } else {
      errors.push(...result.errors)
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, value }
}

/** Throws on failure. For tests and for places a failure really is a bug. */
export function unwrap<T>(result: Result<T>): T {
  if (!result.ok) {
    const detail = result.errors.map((e) => `${e.code}: ${e.message}`).join('; ')
    throw new Error(`Expected a valid result but got errors — ${detail}`)
  }
  return result.value
}
